/// <amd-module name="VSS/Platform/Theming" />
import { expandThemeableProperties, ThemeData } from "VSS/Core/Util/Theming";

import { IVssObservableService, IVssPageContext, IVssPersistantService, ServiceOptions, Services, VssObservableService } from "./Context";

import { IVssContributionService } from "./Contribution";
import { tryGetLocalStorageObject, trySetLocalStorageObject } from "./Util/Storage";

const customUserThemeStorageKey = "user-custom-theme";

/**
 * The ThemingService is designed to manage the current theme for CSS rules. The theming service
 * allows the caller to use logical theme names and get them updated at run-time with the current
 * theme values.
 *
 * Subscribable events:
 *  action: themeChanged -> ThemeData
 */
export interface IVssThemeService extends IVssObservableService<ClientTheme, "themeChanged"> {
    /**
     * getThemeValue can be used to retrieve the currently resolved theme value for a given theme variable.
     * NOTE: If the themeVariable is not defined, the supplied value is returned.
     *
     * @param themeVariable Name of the theme property to resolve (like 'primary-text')
     */
    getThemeValue(themeVariable: string): string;

    /**
     * Gets the current user theme
     */
    getCurrentTheme(): ClientTheme;

    /**
     * Gets the theme with the specified id
     * (must be included through sharedData)
     *
     * @param id Id of the theme to get
     */
    getTheme(id: string): ClientTheme | undefined;

    /**
     * Applies a new theme to the current document
     *
     * @param theme The new theme to apply
     */
    applyTheme(theme: ClientTheme): void;

    /**
     * Saves a custom user theme in local storage so that it is applied for this user on future loads
     *
     * @param theme Custom theme or undefined to clear the custom user theme
     */
    saveCustomUserTheme(theme: ClientTheme | undefined): void;

    /**
     * Adds a CSS style for the given CSS selector to apply the given theme to it.
     *
     * @param selector CSS selector
     * @param theme The theme to apply OR a delegate to compute the theme given the currently selected theme.
     *              If a delegate is provided, it is invoked any time the current user theme is changed and
     *              updates are automatically applied to the style.
     * @param onThemeChanged Optional callback to update this style whenever the currently selected theme changes.
     */
    createThemedSelector(selector: string, theme: ClientTheme | { (newSelectedTheme: ClientTheme): ClientTheme | undefined }): void;
}

export interface ClientTheme {
    id: string;
    name: string;
    data: ThemeData;
    extends?: string;
    isDark?: boolean;
    isPreview?: boolean;
}

interface ThemeDataProviderData {
    requestedThemeId?: string;
}

class VssThemeService extends VssObservableService<ClientTheme, "themeChanged"> implements IVssThemeService, IVssPersistantService {
    private currentTheme: ClientTheme;

    private themeElementsBySelector: { [selector: string]: HTMLStyleElement } = {};

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        // Create a themed CSS style block for the root of the page which always reflects the current theme
        this.createThemedSelector(":root", theme => theme);

        // Make sure the theming service has the current theme initialized with the current data.
        this._initializeTheme();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);

        // Make sure the theming service has the current theme initialized with the current data.
        this._initializeTheme();
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
    }

    public getThemeValue(themePropertyName: string) {
        return expandThemeableProperties(`var(--${themePropertyName})`, this.currentTheme.data);
    }

    public getCurrentTheme(): ClientTheme {
        return this.currentTheme;
    }

    public getTheme(id: string): ClientTheme | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const themesData = contributionService.getSharedData<{ [id: string]: ClientTheme }>("_themes");
        return themesData && themesData[id];
    }

    public applyTheme(theme: ClientTheme) {
        this.currentTheme = theme;
        this._notify(theme, "themeChanged");
    }

    public saveCustomUserTheme(theme: ClientTheme | undefined): void {
        trySetLocalStorageObject(customUserThemeStorageKey, theme);
    }

    public createThemedSelector(selector: string, theme: ClientTheme | { (newSelectedTheme: ClientTheme): ClientTheme | undefined }): void {
        const themeDelegate = typeof theme === "function" ? theme : undefined;

        let themeElement = this.themeElementsBySelector[selector];
        if (!themeElement) {
            themeElement = document.createElement("style");
            themeElement.type = "text/css";
            document.head.appendChild(themeElement);

            this.themeElementsBySelector[selector] = themeElement;

            if (themeDelegate) {
                this.subscribe(value => {
                    const newTheme = themeDelegate(value);
                    const cssContent = `${selector} { ${this.getCSSVariablesContent(newTheme && newTheme.data)} }`;
                    themeElement.innerText = cssContent;
                }, "themeChanged");
            }
        }

        if (this.currentTheme) {
            const themeToApply = themeDelegate ? themeDelegate(this.currentTheme) : (theme as ClientTheme);
            const cssContent = `${selector} { ${this.getCSSVariablesContent(themeToApply && themeToApply.data)} }`;
            themeElement.innerText = cssContent;
        }
    }

    private getCSSVariablesContent(themeData: ThemeData | undefined): string {
        const cssVariables: string[] = [];
        if (themeData) {
            for (const varName in themeData) {
                cssVariables.push(`--${varName}: ${themeData[varName]}`);
            }
        }
        return cssVariables.join("; ");
    }

    private _initializeTheme() {
        let theme: ClientTheme | undefined = undefined;

        // Look in local storage for a custom user theme
        theme = tryGetLocalStorageObject<ClientTheme>(customUserThemeStorageKey);
        if (!theme) {
            // Retrieve the theme data send to the initial page.
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const themeData = contributionService.getData<ThemeDataProviderData>("ms.vss-web.theme-data");
            if (themeData && themeData.requestedThemeId) {
                theme = this.getTheme(themeData.requestedThemeId);
            }
        }

        this.currentTheme = theme || {
            id: "empty-theme",
            name: "",
            data: {}
        };

        if (theme?.id) {
            document.body.classList.add(theme.id.replace(/\./g, '-'))
        }

        this.applyTheme(this.currentTheme);
    }
}

/**
 * Gets the current user theme
 */
export function getCurrentTheme(pageContext: IVssPageContext): ClientTheme {
    return pageContext.getService<IVssThemeService>("IVssThemeService").getCurrentTheme();
}

Services.add("IVssThemeService", {
    serviceFactory: VssThemeService,
    options: ServiceOptions.Persistant | ServiceOptions.Startup
});
